// Veřejná navigační service – operace openRoot, openModal, openDetail, replaceModal,
// openOverlayRoot, closeTop, closeTo

import { useCallback } from 'react';
import { useCurrentLayerId } from '@/shared/lib/layerReload';
import { useNavigationContext } from './useNavigationContext';
import type {
  NavigationKernel,
  OpenDetailOptions,
  OpenLayerOptions,
} from '../core/navigationKernel';
import type { AgendaHandle } from '../core/agendaDefinition';
import type { ParamCodecMap, ParamInput } from '../core/paramCodec';

/**
 * Navigace ZEVNITŘ okna dědí stack zdroje: když je zdrojová vrstva osamostatněná (má `stackId`),
 * nově otevřená (vrchní) vrstva se zařadí do STEJNÉHO floating stacku místo na výchozí plochu.
 * Zdroj z plochy (`stackId === undefined`) i navigace mimo vrstvu (menu/bridge — `sourceLayerId`
 * je `null`) chování nemění: nová vrstva zůstane na ploše. Zrcadlí legacy tok v `MagicControlBridge`.
 */
function inheritSourceStack(kernel: NavigationKernel, sourceLayerId: string | null): void {
  if (!sourceLayerId) return;
  const source = kernel.getLayer(sourceLayerId);
  if (!source?.stackId) return;
  const newTopId = kernel.getState().activeTopLayerId;
  // Přeřaď jen nově vzniklou vrchní vrstvu (ne zdroj samotný — když se nic neotevřelo).
  if (newTopId && newTopId !== sourceLayerId) {
    kernel.setLayerStackId(newTopId, source.stackId);
  }
}

/**
 * Hook pro navigační operace.
 * Vrací typově bezpečné metody pro otevírání a zavírání vrstev.
 *
 * Metody `closeTop`, `closeTo`, `closeLayer`, `openRoot`, `openModal`, `openDetail` a
 * `replaceModal` jsou async – před provedením mohou konzultovat close guardy
 * registrované komponentami přes `useLayerCloseGuard`.
 *
 * Volba mezi `openDetail` a `openModal` je volba ZÁMĚRU navigace:
 * - `openDetail` = výběr v listu (treeview, toolbar listu). Zavře docked vrstvy nad rootem
 *   a otevře vrstvu v detail panelu (list-detail root).
 * - `openModal` = jakákoliv jiná navigace (proklik uvnitř detailu, odkaz na entitu, bridge).
 *   Pushne vrstvu NAD celou agendu, takže je se kam vrátit.
 *
 * `openModal`, `openDetail`, `openOverlayRoot` a `replaceModal` navíc dědí floating stack zdrojové
 * vrstvy (viz `inheritSourceStack`), takže navigace z osamostatněného okna zůstane v jeho stacku.
 * `openRoot` záměrně stack nedědí — je to navigace na úrovni plochy (nahrazuje ji, viz kernel).
 */
export function useNavigator() {
  const { kernel } = useNavigationContext();
  // layerId vrstvy, ze které se hook používá (poskytuje NavigationLayerHost přes LayerReloadScope);
  // `null` při použití mimo vrstvu (shell chrome, menu, testy).
  const sourceLayerId = useCurrentLayerId();

  const openRoot = useCallback(
    async <TOpen extends ParamCodecMap, TState extends ParamCodecMap>(
      agenda: AgendaHandle<TOpen, TState>,
      input: ParamInput<TOpen>,
      options?: OpenLayerOptions<TState>
    ): Promise<void> => {
      await kernel.openRoot(agenda, input, options);
    },
    [kernel]
  );

  const openModal = useCallback(
    async <TOpen extends ParamCodecMap, TState extends ParamCodecMap>(
      agenda: AgendaHandle<TOpen, TState>,
      input: ParamInput<TOpen>,
      options?: OpenLayerOptions<TState>
    ): Promise<void> => {
      await kernel.openModal(agenda, input, options);
      inheritSourceStack(kernel, sourceLayerId);
    },
    [kernel, sourceLayerId]
  );

  const openDetail = useCallback(
    async <TOpen extends ParamCodecMap, TState extends ParamCodecMap>(
      agenda: AgendaHandle<TOpen, TState>,
      input: ParamInput<TOpen>,
      options?: OpenDetailOptions<TState>
    ): Promise<void> => {
      await kernel.openDetail(agenda, input, options);
      inheritSourceStack(kernel, sourceLayerId);
    },
    [kernel, sourceLayerId]
  );

  const replaceModal = useCallback(
    async <TOpen extends ParamCodecMap, TState extends ParamCodecMap>(
      agenda: AgendaHandle<TOpen, TState>,
      input: ParamInput<TOpen>,
      options?: OpenLayerOptions<TState>
    ): Promise<void> => {
      await kernel.replaceModal(agenda, input, options);
      inheritSourceStack(kernel, sourceLayerId);
    },
    [kernel, sourceLayerId]
  );

  const openOverlayRoot = useCallback(
    async <TOpen extends ParamCodecMap, TState extends ParamCodecMap>(
      agenda: AgendaHandle<TOpen, TState>,
      input: ParamInput<TOpen>,
      options?: OpenLayerOptions<TState>
    ): Promise<void> => {
      await kernel.openOverlayRoot(agenda, input, options);
      inheritSourceStack(kernel, sourceLayerId);
    },
    [kernel, sourceLayerId]
  );

  const closeTop = useCallback(async (): Promise<void> => {
    await kernel.closeTop();
  }, [kernel]);

  const closeTo = useCallback(
    async (layerId: string): Promise<void> => {
      await kernel.closeTo(layerId);
    },
    [kernel]
  );

  const closeLayer = useCallback(
    async (layerId: string): Promise<void> => {
      await kernel.closeLayer(layerId);
    },
    [kernel]
  );

  return {
    openRoot,
    openModal,
    openDetail,
    replaceModal,
    openOverlayRoot,
    closeTop,
    closeTo,
    closeLayer,
  };
}
